import React, { useCallback, useEffect, useRef, useState } from "react";
import {
    Box,
    ButtonBase,
    Stack,
    Typography,
    useTheme,
} from "@mui/material";
import { alpha, darken, lighten } from "@mui/material/styles";
import CardMembershipRounded from "@mui/icons-material/CardMembershipRounded";
import HourglassEmptyRounded from "@mui/icons-material/HourglassEmptyRounded";
import CalendarMonthRounded from "@mui/icons-material/CalendarMonthRounded";
import WorkspacePremiumRounded from "@mui/icons-material/WorkspacePremiumRounded";
import CheckRounded from "@mui/icons-material/CheckRounded";
import LocalOfferRounded from "@mui/icons-material/LocalOfferRounded";
import BrandSubscription from "../../../api/BrandSubscription";
import { useCookies } from "react-cookie";
import BottomButton from "../../BottomButton";
import { isFirstSetupProps } from "../../../interfaces/isFirstSetupProps";
import ProgressHeader from "../../FirstSetup/ProgressHeader";
import { formatPriceCents } from "../../../utils/priceFormatter";
import Subscription from "../../../api/Subscription";
import { useNavigate } from "react-router-dom";
import { getEnvironment } from "../../../utils/getEnvironment";
import Order from "../../../api/Order";
import WFPCheckout from "../../../api/WFPCheckout";
import { loadScript } from "../../../utils/loadScript";
import Brand from "../../../api/Brand";
import { FullScreenLoader } from "../../FullScreenLoader";
import ColoredIcon from "../../ColoredIcon";
import BackIcon from "../../../img/Arrow_Left.svg";
import { FONTS, CARD_STYLES } from "../../../constants/styles";

const env = getEnvironment();

const NOISE_AFTER = {
    content: '""',
    position: "absolute" as const,
    inset: 0,
    opacity: 0.04,
    backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.65' numOctaves='3' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)'/%3E%3C/svg%3E")`,
    backgroundRepeat: "repeat",
    backgroundSize: "128px 128px",
    pointerEvents: "none" as const,
};

const PLAN_ICON: Record<number, React.ReactNode> = {
    14: <HourglassEmptyRounded sx={{ fontSize: 20 }} />,
    30: <CalendarMonthRounded sx={{ fontSize: 20 }} />,
    365: <WorkspacePremiumRounded sx={{ fontSize: 20 }} />,
};

const Subscriptions: React.FC<isFirstSetupProps> = ({
    isFirstSetup = false,
}) => {
    const [subscriptions, setSubscriptions] = useState<Subscription[]>([]);
    const [selected, setSelected] = useState<Subscription | undefined>();
    const [activeSubscription, setActiveSubscription] =
        useState<BrandSubscription>();
    const [isExpired, setIsExpired] = useState(false);
    const [loading, setLoading] = useState(true);

    // Affiliate info
    const [affiliateDiscount, setAffiliateDiscount] = useState<number | null>(
        null
    );
    const [affiliateBrandName, setAffiliateBrandName] = useState<string | null>(
        null
    );

    // Handle WFP script loading
    const [wfpLoaded, setWfpLoaded] = useState(false);
    const [wfpError, setWfpError] = useState(false);

    const theme = useTheme();
    const navigate = useNavigate();
    const primary = theme.palette.primary.main;

    const scrollRef = useRef<HTMLDivElement>(null);
    const heroRef = useRef<HTMLDivElement>(null);
    const [scrolledPastHero, setScrolledPastHero] = useState(false);

    const handleScroll = useCallback(() => {
        const el = scrollRef.current;
        const heroEl = heroRef.current;
        if (!el || !heroEl) return;
        setScrolledPastHero(el.scrollTop > heroEl.offsetHeight - 80);
    }, []);

    const [cookies] = useCookies(["uuid", "brand_id"]);

    const getCredentials = useCallback(() => {
        const { uuid, brand_id: brandId } = cookies;
        if (!uuid || !brandId) navigate("/");
        return [uuid, brandId];
    }, [cookies, navigate]);

    // Load WFP script
    useEffect(() => {
        loadScript(
            "https://secure.wayforpay.com/server/pay-widget.js",
            "widget-wfp-script"
        )
            .then(() => {
                console.log("WayForPay script loaded successfully");
                setWfpLoaded(true);
            })
            .catch((err) => {
                console.error("WayForPay script failed to load:", err);
                setWfpError(true);
            });
    }, []);

    // Fetch subscriptions and active subscription
    useEffect(() => {
        const fetchSubscriptions = async () => {
            try {
                const subscriptionList = await Subscription.list();
                setSubscriptions(subscriptionList);

                const [uuid, brandId] = getCredentials();
                if (uuid && brandId) {
                    const discountInfo = await Brand.affiliateDiscountInfo(
                        uuid,
                        brandId
                    );
                    if (discountInfo) {
                        setAffiliateDiscount(discountInfo.discount);
                        setAffiliateBrandName(discountInfo.name);
                    }
                }

                if (!isFirstSetup) {
                    const activeSub = await BrandSubscription.info(
                        uuid,
                        brandId
                    );
                    if (activeSub && Object.keys(activeSub).length > 0) {
                        setActiveSubscription(activeSub);
                        setIsExpired(!activeSub.valid);

                        if (activeSub.duration === 14) {
                            // If user already used the trial, preselect the next available paid subscription
                            setSelected(
                                subscriptionList.filter(
                                    (sub) => sub.duration !== 14
                                )[0]
                            );
                        } else {
                            // Pre-select the current subscription if valid (not sure if needed)
                            const currentPaid = subscriptionList.find(
                                (sub) => sub.key === activeSub.subscription
                            );
                            setSelected(
                                currentPaid ||
                                    subscriptionList.filter(
                                        (sub) => sub.duration !== 14
                                    )[0]
                            );
                        }
                    }
                } else {
                    setSelected(subscriptionList[0]);
                }
            } catch (error) {
                console.error("Error fetching subscriptions:", error);
            } finally {
                setLoading(false);
            }
        };

        fetchSubscriptions();
    }, [isFirstSetup, getCredentials]);

    useEffect(() => {
        console.log("Active: ", activeSubscription);
        console.log("Sub options: ", subscriptions);
    }, [activeSubscription, subscriptions]);

    /* // Stripe checkout
    const handlePaymentStripe = useCallback(async () => {
        const [uuid, brandId] = getCredentials();
        if (!selected) return;

        if (selected.duration === 14) {
            // For trial, just update and proceed
            await BrandSubscription.register(uuid, brandId, selected.key);
            navigate(isFirstSetup ? "/setup/save" : "/");
        } else {
            // For paid, create checkout session
            const response = await StripeCheckout.createSession(
                uuid,
                brandId,
                selected.key,
                `${window.location.origin}/${
                    isFirstSetup ? "setup/save" : "success"
                }`, // Success URL
                `${window.location.origin}/cancel` // Cancel URL
            );
            if (response) {
                const { checkoutUrl } = response;
                if (checkoutUrl) {
                    window.location.href = checkoutUrl;
                }
            } else {
                console.error("Failed to create Stripe session");
            }
        }
    }, [selected, isFirstSetup, getCredentials, navigate]); */

    // WayForPay checkout
    // OPERATES ON HARDCODED UAH PRICES FOR NOW :|
    const handlePaymentW4P = useCallback(async () => {
        if (wfpError) window.location.reload();
        const [uuid, brandId] = getCredentials();
        if (!selected) return;

        if (selected.duration === 14 && !isFirstSetup) {
            console.warn(
                "Attempted to reuse trial subscription after initial setup."
            );
            return;
        } else if (selected.duration === 14 && isFirstSetup) {
            // For trial, just update and proceed
            await BrandSubscription.register(uuid, brandId, selected.key);
            navigate(isFirstSetup ? "/setup/save" : "/");
        } else {
            // For paid, proceed to checkout session

            // Generate a consistent timestamp
            const orderDate = Math.floor(Date.now() / 1000).toString();
            const orderReference = `${uuid}-${selected.key}-${orderDate}`;

            try {
                // Step 1: Create order in the backend
                const orderResponse = await Order.create(
                    uuid,
                    brandId,
                    selected.key,
                    orderReference
                );

                if (!orderResponse || !orderResponse.success) {
                    console.error("Failed to create order in backend");
                    return;
                }

                // Step 2: Fetch merchantSignature from the backend
                const originalPrice = selected.duration === 30 ? 1300 : 13000;
                const discountedRaw = affiliateDiscount
                    ? originalPrice * (1 - affiliateDiscount / 100)
                    : originalPrice;

                const discountedPrice = Math.round(discountedRaw); // For signature (integer)
                const subPrice = discountedPrice.toFixed(2); // For WayForPay run (string)

                const signature = await WFPCheckout.getSignature(
                    "jemloc_com",
                    "https://jemloc.com",
                    orderReference,
                    orderDate,
                    //Number(formatPrice(selected.price)),
                    discountedPrice,
                    //1, // For testing
                    "UAH",
                    [selected.name],
                    [1],
                    [discountedPrice]
                );

                if (signature) {
                    // Step 3: Initialize WayForPay widget
                    const wayforpay = new (window as any).Wayforpay();
                    if (wayforpay) console.log("Wayforpay script detected");
                    else console.log("No wayfopay script detected");

                    wayforpay.run(
                        {
                            merchantAccount: "jemloc_com",
                            merchantDomainName: "https://jemloc.com",
                            authorizationType: "SimpleSignature",
                            merchantSignature: signature,
                            orderReference,
                            orderDate,
                            amount: subPrice,
                            //amount: "1.00", // For testing
                            currency: "UAH",
                            productName: [`${selected.name}`],
                            productCount: [1],
                            productPrice: [subPrice],
                            //clientFirstName: "Client", // Replace with actual data
                            //clientLastName: "Name", // Replace with actual data
                            clientEmail: "client@example.com", // Replace with actual data
                            clientPhone: "380631234567", // Replace with actual data
                            language: "UA",
                            //serviceUrl: `https://server.jemloc.com/api/admin/v1/wfp/webhook`, // For testing
                            serviceUrl: `${env.api_url}/wfp/webhook`,
                            regularOn: 1,
                            regularAmount: subPrice,
                            regularMode:
                                selected.duration === 30 ? "monthly" : "yearly",
                            regularCount: selected.duration === 30 ? 60 : 5,
                            regularBehavior: "preset",
                            makeRedirect: true, // Force redirect instead of modal in all contexts (including desktop)
                        },
                        (response: any) => {
                            // On approved
                            console.log("Payment approved:", response);
                            navigate("/success");
                        },
                        (response: any) => {
                            // On declined
                            console.log("Payment declined:", response);
                            navigate("/cancel");
                        },
                        (response: any) => {
                            // On pending or processing
                            console.log("Payment pending:", response);
                        }
                    );
                } else {
                    console.error("Failed to fetch merchant signature");
                }
            } catch (error) {
                console.error("Error during payment:", error);
            }
        }
    }, [
        selected,
        getCredentials,
        navigate,
        isFirstSetup,
        affiliateDiscount,
        wfpError,
    ]);

    const onExit = () => navigate(isFirstSetup ? "/setup/save" : "/");

    const filterSubscriptionsToDisplay = (
        subs: Subscription[],
        firstSetup: boolean,
        expired: boolean,
        activeSub?: BrandSubscription
    ): Subscription[] => {
        if (firstSetup) return subs;
        if (expired || !firstSetup)
            return subs.filter((sub) => sub.duration !== 14);
        if (activeSub)
            return subs.filter(
                (sub) =>
                    sub.duration !== 14 &&
                    sub.price !== activeSub.price
            );
        console.warn(
            "Unexpected state: No valid case for filtering subscriptions."
        );
        return subs; // Shouldn't be a valid case, just a fallback
    };

    // --- Derived values ---
    const hasActiveSub = !isFirstSetup && !!activeSubscription;
    const activePlanName = hasActiveSub
        ? subscriptions.find((sub) => activeSubscription!.price === sub.price)
              ?.name
        : undefined;

    const sectionLabel = isFirstSetup
        ? "Choose a plan"
        : isExpired
        ? "Reactivate"
        : "Upgrade";

    const filteredPlans = filterSubscriptionsToDisplay(
        subscriptions,
        isFirstSetup,
        isExpired,
        activeSubscription
    );

    const buttonText = (() => {
        if (wfpError) return "Try again";
        if (!selected) return "Select a plan";
        if (selected.duration === 14) return "Start Free Trial";
        const price = affiliateDiscount
            ? formatPriceCents(selected.price * (1 - affiliateDiscount / 100))
            : formatPriceCents(selected.price);
        return affiliateDiscount
            ? `Pay €${price} (Discounted)`
            : `Pay €${price}`;
    })();

    // --- Hero gradient ---
    const heroGradient = hasActiveSub && isExpired
        ? `linear-gradient(135deg, ${alpha(primary, 0.5)} 0%, ${darken(primary, 0.3)} 50%, ${darken(primary, 0.45)} 100%)`
        : `linear-gradient(180deg, ${lighten(primary, 0.08)} 0%, ${darken(primary, 0.08)} 100%)`;

    const showBackArrow = hasActiveSub && !isExpired;

    if (loading) return <FullScreenLoader />;

    return (
        <>
            {isFirstSetup && <ProgressHeader step={7} title="Payments" />}

            <Stack
                ref={scrollRef}
                onScroll={handleScroll}
                direction="column"
                width="100%"
                flex={1}
                sx={{
                    overflowY: "auto",
                    overflowX: "hidden",
                    bgcolor: "white",
                }}
            >
                {/* ── Hero ── */}
                <Stack
                    ref={heroRef}
                    width="100%"
                    alignItems="center"
                    justifyContent="center"
                    sx={{
                        background: heroGradient,
                        pt: hasActiveSub ? "48px" : "40px",
                        pb: hasActiveSub ? "40px" : "32px",
                        px: 2,
                        boxSizing: "border-box",
                        position: "relative",
                        zIndex: 0,
                        "&::after": NOISE_AFTER,
                    }}
                >
                    {/* Back arrow — only on active (non-expired) */}
                    {showBackArrow && (
                        <ButtonBase
                            onClick={onExit}
                            sx={{
                                position: "absolute",
                                top: 12,
                                left: 6,
                                zIndex: 2,
                            }}
                        >
                            <ColoredIcon
                                icon={BackIcon}
                                color="white"
                                size="38px"
                            />
                        </ButtonBase>
                    )}

                    {hasActiveSub ? (
                        /* Active / Expired — frosted glass card */
                        <Stack
                            className="force-dynamic-translation"
                            sx={{
                                position: "relative",
                                zIndex: 1,
                                bgcolor: "rgba(255,255,255,0.12)",
                                backdropFilter: "blur(16px)",
                                borderRadius: "16px",
                                border: "1px solid rgba(255,255,255,0.18)",
                                p: 2,
                                width: "100%",
                                maxWidth: 340,
                                boxSizing: "border-box",
                            }}
                        >
                            {/* Top row: icon + name/price + status pill */}
                            <Stack
                                direction="row"
                                alignItems="center"
                                spacing={1.5}
                            >
                                <Box
                                    sx={{
                                        width: 40,
                                        height: 40,
                                        borderRadius: "12px",
                                        bgcolor: "rgba(255,255,255,0.15)",
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        flex: "0 0 auto",
                                        color: "white",
                                    }}
                                >
                                    {PLAN_ICON[activeSubscription!.duration] || (
                                        <CardMembershipRounded
                                            sx={{ fontSize: 20 }}
                                        />
                                    )}
                                </Box>
                                <Stack flex={1} spacing={0.125}>
                                    <Typography
                                        fontFamily={FONTS.display}
                                        fontSize="16px"
                                        fontWeight={700}
                                        color="white"
                                        lineHeight={1.2}
                                        sx={{ textTransform: "capitalize" }}
                                    >
                                        {activePlanName}
                                    </Typography>
                                    <Typography
                                        fontSize="12px"
                                        color="rgba(255,255,255,0.7)"
                                    >
                                        {activeSubscription!.trial
                                            ? "Free trial"
                                            : activeSubscription!.price > 0
                                            ? `€${formatPriceCents(activeSubscription!.price)} / ${activeSubscription!.duration === 365 ? "year" : "month"}`
                                            : "Free"}
                                    </Typography>
                                </Stack>
                                {/* Status pill */}
                                <Box
                                    sx={{
                                        bgcolor: isExpired
                                            ? "rgba(244,67,54,0.25)"
                                            : "rgba(76,175,80,0.25)",
                                        backdropFilter: "blur(8px)",
                                        borderRadius: "12px",
                                        px: 1.25,
                                        py: 0.375,
                                        flexShrink: 0,
                                    }}
                                >
                                    <Typography
                                        fontSize="11px"
                                        fontWeight={600}
                                        color="white"
                                    >
                                        {isExpired ? "Expired" : "Active"}
                                    </Typography>
                                </Box>
                            </Stack>

                            {/* Separator */}
                            <Box
                                sx={{
                                    height: "1px",
                                    bgcolor: "rgba(255,255,255,0.15)",
                                    my: 1.5,
                                }}
                            />

                            {/* Date row */}
                            <Stack
                                direction="row"
                                alignItems="center"
                                justifyContent="space-between"
                            >
                                <Typography
                                    fontSize="12px"
                                    color="rgba(255,255,255,0.6)"
                                >
                                    {isExpired ? "Expired on" : "Valid until"}
                                </Typography>
                                <Typography
                                    fontSize="13px"
                                    fontWeight={600}
                                    color="white"
                                >
                                    {new Date(
                                        activeSubscription!.finished
                                    ).toLocaleDateString("en-GB")}
                                </Typography>
                            </Stack>
                        </Stack>
                    ) : (
                        /* First setup — compact decorative hero */
                        <Stack
                            alignItems="center"
                            spacing={1}
                            sx={{ position: "relative", zIndex: 1 }}
                        >
                            <Box
                                sx={{
                                    width: 48,
                                    height: 48,
                                    borderRadius: "50%",
                                    bgcolor: "rgba(255,255,255,0.15)",
                                    backdropFilter: "blur(8px)",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    color: "white",
                                }}
                            >
                                <CardMembershipRounded
                                    sx={{ fontSize: 24 }}
                                />
                            </Box>
                            <Box
                                sx={{
                                    bgcolor: "rgba(255,255,255,0.15)",
                                    backdropFilter: "blur(8px)",
                                    borderRadius: "12px",
                                    px: 1.25,
                                    py: 0.25,
                                }}
                            >
                                <Typography
                                    fontSize="12px"
                                    fontWeight={600}
                                    color="white"
                                >
                                    Subscription
                                </Typography>
                            </Box>
                        </Stack>
                    )}
                </Stack>

                {/* ── Sticky toolbar (bottom sheet top) ── */}
                <Box
                    sx={{
                        position: "sticky",
                        top: 0,
                        zIndex: 4,
                        bgcolor: "white",
                        borderTopLeftRadius: "24px",
                        borderTopRightRadius: "24px",
                        mt: "-24px",
                        pt: 3,
                    }}
                >
                    <Stack
                        direction="row"
                        alignItems="center"
                        justifyContent="center"
                        sx={{
                            maxHeight: scrolledPastHero ? 52 : 0,
                            opacity: scrolledPastHero ? 1 : 0,
                            overflow: "hidden",
                            transition:
                                "max-height 0.25s ease, opacity 0.2s ease",
                            px: 1,
                            borderBottom: scrolledPastHero
                                ? "1px solid rgba(0,0,0,0.06)"
                                : "none",
                            position: "relative",
                        }}
                    >
                        {showBackArrow && (
                            <ButtonBase
                                onClick={onExit}
                                sx={{
                                    position: "absolute",
                                    left: 6,
                                }}
                            >
                                <ColoredIcon
                                    icon={BackIcon}
                                    color="black"
                                    size="38px"
                                />
                            </ButtonBase>
                        )}
                        <Typography
                            fontFamily={FONTS.display}
                            fontSize="18px"
                            fontWeight={600}
                            py={1}
                        >
                            Subscription
                        </Typography>
                    </Stack>
                </Box>

                {/* ── Content (bottom sheet body) ── */}
                <Stack
                    spacing={2}
                    sx={{
                        position: "relative",
                        zIndex: 1,
                        bgcolor: "white",
                        px: 2,
                        pt: 0,
                        pb: "calc(4vh + 90px + 16vh)",
                        boxSizing: "border-box",
                        "@keyframes fadeSlideIn": {
                            from: {
                                opacity: 0,
                                transform: "translateY(12px)",
                            },
                            to: {
                                opacity: 1,
                                transform: "translateY(0)",
                            },
                        },
                        animation: "fadeSlideIn 0.3s ease-out",
                    }}
                >
                    {/* Affiliate discount banner */}
                    {affiliateBrandName && affiliateDiscount && (
                        <Stack
                            direction="row"
                            alignItems="center"
                            spacing={1.5}
                            sx={{
                                ...CARD_STYLES,
                                bgcolor: alpha(
                                    theme.palette.success.main,
                                    0.06
                                ),
                            }}
                        >
                            <Box
                                sx={{
                                    width: 40,
                                    height: 40,
                                    borderRadius: "12px",
                                    bgcolor: alpha(
                                        theme.palette.success.main,
                                        0.12
                                    ),
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    flex: "0 0 auto",
                                    color: theme.palette.success.main,
                                }}
                            >
                                <LocalOfferRounded sx={{ fontSize: 20 }} />
                            </Box>
                            <Stack>
                                <Typography
                                    fontSize="14px"
                                    fontWeight={600}
                                    color="success.main"
                                >
                                    {affiliateDiscount}% Partner Discount
                                </Typography>
                                <Typography
                                    fontSize="12px"
                                    color="text.secondary"
                                >
                                    Thanks to{" "}
                                    <strong>{affiliateBrandName}</strong>
                                </Typography>
                            </Stack>
                        </Stack>
                    )}

                    {/* Section label */}
                    <Typography
                        fontSize="11px"
                        fontWeight={600}
                        color="text.secondary"
                        textTransform="uppercase"
                        letterSpacing="0.5px"
                    >
                        {sectionLabel}
                    </Typography>

                    {/* Plan cards */}
                    <Stack spacing={1.5}>
                        {filteredPlans.map((sub, index) => (
                            <PlanCard
                                key={sub.key}
                                subscription={sub}
                                isSelected={selected?.key === sub.key}
                                onSelect={setSelected}
                                isFirstSetup={isFirstSetup}
                                affiliateDiscount={affiliateDiscount}
                                index={index}
                            />
                        ))}
                    </Stack>

                    {/* WFP error */}
                    {wfpError && (
                        <Typography
                            textAlign="center"
                            color="error.main"
                            fontSize="13px"
                        >
                            Payments are temporary unavailable. Apologies
                            for the inconvenience
                        </Typography>
                    )}

                    {/* Terms & Conditions */}
                    <Typography
                        textAlign="center"
                        fontSize="12px"
                        color="text.secondary"
                        onClick={() =>
                            (window.location.href =
                                "https://jemloc.com/terms")
                        }
                        sx={{ cursor: "pointer" }}
                    >
                        Terms & Conditions
                    </Typography>
                </Stack>
            </Stack>

            {/* ── Bottom button ── */}
            <BottomButton
                onClick={handlePaymentW4P}
                width="80%"
                disabled={!wfpLoaded || (!wfpError && !selected)}
                sticky
            >
                {buttonText}
            </BottomButton>
        </>
    );
};

// ── PlanCard ──

const PlanCard = (props: {
    subscription: Subscription;
    isSelected: boolean;
    onSelect: (val: Subscription) => void;
    isFirstSetup: boolean;
    affiliateDiscount: number | null;
    index: number;
}) => {
    const theme = useTheme();
    const primary = theme.palette.primary.main;
    const { subscription, isSelected, onSelect, isFirstSetup, affiliateDiscount, index } =
        props;

    const isTrial = subscription.duration === 14;
    const isYearly = subscription.duration === 365;

    // Recommended emphasis background
    const emphasisBg = (() => {
        if (isSelected) return alpha(primary, 0.05);
        if (isTrial && isFirstSetup) return alpha(theme.palette.success.main, 0.04);
        if (isYearly && !isFirstSetup)
            return `linear-gradient(135deg, ${alpha(primary, 0.03)} 0%, ${alpha(primary, 0.07)} 100%)`;
        return "white";
    })();

    // Badge
    const badge = (() => {
        if (isTrial && isFirstSetup) return "Recommended to start";
        if (isYearly && !isFirstSetup) return "Best value";
        return null;
    })();

    const features = subscription.description
        .split(".")
        .filter((x) => x.trim() !== "");

    const priceDisplay = (() => {
        if (isTrial) return { text: "Free", color: theme.palette.success.main };
        const cents = affiliateDiscount
            ? subscription.price * (1 - affiliateDiscount / 100)
            : subscription.price;
        return {
            text: `€${formatPriceCents(cents)}`,
            color: primary,
        };
    })();

    const originalPrice =
        !isTrial && affiliateDiscount
            ? `€${formatPriceCents(subscription.price)}`
            : null;

    const checkColor = isSelected ? primary : "text.disabled";

    return (
        <ButtonBase
            className="force-dynamic-translation"
            onClick={() => onSelect(subscription)}
            sx={{
                display: "block",
                width: "100%",
                textAlign: "left",
                borderRadius: "16px",
                "&:active .plan-card-inner": {
                    transform: "scale(0.98)",
                },
                "@keyframes planCardIn": {
                    from: { opacity: 0, transform: "translateY(16px)" },
                    to: { opacity: 1, transform: "translateY(0)" },
                },
                animation: `planCardIn 0.35s ease-out ${index * 50}ms both`,
            }}
        >
            <Stack
                className="plan-card-inner"
                sx={{
                    ...CARD_STYLES,
                    background: emphasisBg,
                    border: isSelected
                        ? `2px solid ${alpha(primary, 0.5)}`
                        : "2px solid transparent",
                    transition:
                        "border-color 0.2s ease, background 0.2s ease, transform 0.15s ease",
                }}
            >
                {/* Top row: icon + name + badge + price */}
                <Stack
                    direction="row"
                    alignItems="center"
                    spacing={1.5}
                >
                    <Box
                        sx={{
                            width: 40,
                            height: 40,
                            borderRadius: "12px",
                            bgcolor: alpha(primary, 0.1),
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            flex: "0 0 auto",
                            color: primary,
                        }}
                    >
                        {PLAN_ICON[subscription.duration] || (
                            <CardMembershipRounded sx={{ fontSize: 20 }} />
                        )}
                    </Box>
                    <Stack flex={1} spacing={0.5}>
                        <Typography
                            fontFamily={FONTS.display}
                            fontSize="15px"
                            fontWeight={600}
                        >
                            {subscription.name}
                        </Typography>
                        {badge && (
                            <Box
                                sx={{
                                    alignSelf: "flex-start",
                                    bgcolor: isTrial
                                        ? alpha(theme.palette.success.main, 0.12)
                                        : alpha(primary, 0.12),
                                    borderRadius: "8px",
                                    px: 0.875,
                                    py: 0.25,
                                }}
                            >
                                <Typography
                                    fontSize="10px"
                                    fontWeight={700}
                                    letterSpacing="0.3px"
                                    textTransform="uppercase"
                                    color={
                                        isTrial
                                            ? theme.palette.success.main
                                            : primary
                                    }
                                >
                                    {badge}
                                </Typography>
                            </Box>
                        )}
                    </Stack>
                    <Stack alignItems="flex-end" flexShrink={0}>
                        <Typography
                            fontFamily={FONTS.display}
                            fontSize="18px"
                            fontWeight={700}
                            color={priceDisplay.color}
                        >
                            {priceDisplay.text}
                        </Typography>
                        {!isTrial && (
                            <Typography
                                fontSize="11px"
                                color="text.secondary"
                            >
                                per{" "}
                                {subscription.duration === 30
                                    ? "month"
                                    : "year"}
                            </Typography>
                        )}
                    </Stack>
                </Stack>

                {/* Features */}
                {features.length > 0 && (
                    <Stack spacing={0.5} mt={1.5} pl={0.5}>
                        {features.map((feat, i) => (
                            <Stack
                                key={i}
                                direction="row"
                                alignItems="center"
                                spacing={1}
                            >
                                <CheckRounded
                                    sx={{
                                        fontSize: 14,
                                        color: checkColor,
                                        transition: "color 0.2s ease",
                                    }}
                                />
                                <Typography
                                    fontSize="12px"
                                    color="text.secondary"
                                >
                                    {feat.trim()}
                                </Typography>
                            </Stack>
                        ))}
                    </Stack>
                )}

                {/* Original price struck through (when discounted) */}
                {originalPrice && (
                    <Typography
                        fontSize="12px"
                        color="text.disabled"
                        sx={{
                            textDecoration: "line-through",
                            mt: 1,
                            pl: 5.5,
                        }}
                    >
                        {originalPrice}
                    </Typography>
                )}
            </Stack>
        </ButtonBase>
    );
};

export default Subscriptions;
